// components/schedule/ScheduleDialog/useScheduleConflict.ts

import { useState, useEffect } from 'react';
import { Schedule } from '../../../types/schedule';
import { OtherSchedule } from '../../../types/otherSchedule';
import { scheduleService } from '../../../services/schedule';
import { otherScheduleService } from '../../../services/otherSchedule';
import { Car } from '../../../types/car';

export const useScheduleConflict = (
  scheduleType: 'construction' | 'other',
  constructionFormData: Schedule,
  otherFormData: OtherSchedule
) => {
  // 他の予定と重複しているメンバーID
  const [membersWithOtherSchedules, setMembersWithOtherSchedules] = useState<number[]>([]);
  
  // 他の予定と重複している車両ID
  const [vehiclesWithOtherSchedules, setVehiclesWithOtherSchedules] = useState<number[]>([]);
  
  // 現在の日付に基づいて予定重複をチェック
  useEffect(() => {
    const currentDate = scheduleType === 'construction'
      ? constructionFormData.scheduleDate
      : otherFormData.scheduleDate;

    if (currentDate) {
      checkMembersScheduleStatus(currentDate);
      checkVehiclesScheduleStatus(currentDate); // 車両のスケジュール状況も確認
    }
  }, [scheduleType, constructionFormData.scheduleDate, otherFormData.scheduleDate]);

  // 特定日付の全スケジュールを取得
  const fetchDailySchedules = async (date: string) => {
    try {
      const startDate = date;
      const endDate = date;
      // constructionSchedules と otherSchedules を両方取得
      const [constructionData, otherData] = await Promise.all([
        scheduleService.getSchedulesByDateRange(startDate, endDate),
        otherScheduleService.getOtherSchedulesByDateRange(startDate, endDate)
      ]);

      return {
        construction: constructionData.schedules || [],
        other: otherData.schedules || []
      };
    } catch (error) {
      console.error('Error fetching daily schedules:', error);
      return { construction: [], other: [] };
    }
  };

  // 同じ日付の全メンバーの予定状況をチェックする関数
  const checkMembersScheduleStatus = async (date: string) => {
    if (!date) return;

    try {
      const dailySchedules = await fetchDailySchedules(date);

      // 現在編集中の予定ID
      const currentId = scheduleType === 'construction'
        ? constructionFormData.id
        : otherFormData.id;

      // 他の予定に登録されているメンバーIDを収集
      const memberIds: number[] = [];

      // 工事予定をチェック
      if (dailySchedules.construction && Array.isArray(dailySchedules.construction)) {
        dailySchedules.construction.forEach((schedule: Schedule) => {
          if (schedule.id !== currentId && schedule.memberData) {
            schedule.memberData.forEach((m: any) => {
              if (m.Member_ID) memberIds.push(m.Member_ID);
            });
          }
        });
      }

      // その他予定をチェック
      if (dailySchedules.other && Array.isArray(dailySchedules.other)) {
        dailySchedules.other.forEach((schedule: OtherSchedule) => {
          if (schedule.id !== currentId && schedule.memberData) {
            schedule.memberData.forEach((m: any) => {
              if (m.Member_ID) memberIds.push(m.Member_ID);
            });
          }
        });
      }

      // 重複を除去して状態を更新
      const uniqueMemberIds = memberIds.filter((id, index) => {
        return memberIds.indexOf(id) === index;
      });

      setMembersWithOtherSchedules(uniqueMemberIds);
    } catch (error) {
      console.error('Error checking members schedule status:', error);
    }
  };

  // 同じ日付の全車両の予定状況をチェックする関数
  const checkVehiclesScheduleStatus = async (date: string) => {
    if (!date) return;

    try {
      const dailySchedules = await fetchDailySchedules(date);

      // 現在編集中の予定ID
      const currentId = scheduleType === 'construction'
        ? constructionFormData.id
        : otherFormData.id;

      // 他の予定に登録されている車両IDを収集
      const vehicleIds: number[] = [];

      // 工事予定をチェック
      if (dailySchedules.construction && Array.isArray(dailySchedules.construction)) {
        dailySchedules.construction.forEach((schedule: Schedule) => {
          if (schedule.id !== currentId && schedule.vehicleData) {
            schedule.vehicleData.forEach((v: any) => {
              if (v.Car_ID) vehicleIds.push(v.Car_ID);
            });
          }
        });
      }

      // その他予定をチェック
      if (dailySchedules.other && Array.isArray(dailySchedules.other)) {
        dailySchedules.other.forEach((schedule: OtherSchedule) => {
          if (schedule.id !== currentId && schedule.vehicleData) {
            schedule.vehicleData.forEach((v: any) => {
              if (v.Car_ID) vehicleIds.push(v.Car_ID);
            });
          }
        });
      }

      // 重複を除去して状態を更新
      const uniqueVehicleIds = vehicleIds.filter((id, index) => {
        return vehicleIds.indexOf(id) === index;
      });

      setVehiclesWithOtherSchedules(uniqueVehicleIds);
    } catch (error) {
      console.error('Error checking vehicles schedule status:', error);
    }
  };

  // メンバー選択時の重複チェックと確認
  const checkMemberConflict = async (member: any) => {
    // 現在編集中の予定のID（新規の場合はundefined）
    const currentScheduleId = scheduleType === 'construction'
      ? constructionFormData.id
      : otherFormData.id;

    // 現在の日付
    const currentDate = scheduleType === 'construction'
      ? constructionFormData.scheduleDate
      : otherFormData.scheduleDate;

    if (!currentDate) return true; // 日付がない場合は競合なしとみなす

    // 同じ日付の全ての予定を取得
    const dailySchedules = await fetchDailySchedules(currentDate);

    // 同じメンバーが登録されている他の予定を検索
    const conflictingSchedules = [];

    // 工事予定をチェック
    for (const schedule of dailySchedules.construction) {
      if (schedule.id !== currentScheduleId && schedule.memberData &&
        schedule.memberData.some((m: any) => m.Member_ID === member.ID)) {
        conflictingSchedules.push({
          type: '工事予定',
          title: schedule.siteName || '(工事名なし)',
          time: schedule.isAllDay ? '終日' : `${schedule.startTime || ''}～${schedule.endTime || ''}`
        });
      }
    }

    // その他予定をチェック
    for (const schedule of dailySchedules.other) {
      if (schedule.id !== currentScheduleId && schedule.memberData &&
        schedule.memberData.some((m: any) => m.Member_ID === member.ID)) {
        conflictingSchedules.push({
          type: 'その他予定',
          title: schedule.scheduleContent || '(内容なし)',
          time: schedule.isAllDay ? '終日' : `${schedule.startTime || ''}～${schedule.endTime || ''}`
        });
      }
    }

    // 競合がある場合は警告を表示
    if (conflictingSchedules.length > 0) {
      const confirmAdd = window.confirm(
        `${member.LastName}さんは既に同日の以下の予定に登録されています:\n\n` +
        conflictingSchedules.map(s => `・${s.type}: ${s.title} (${s.time})`).join('\n') +
        '\n\n追加しますか？'
      );

      return confirmAdd; // 確認結果を返す
    }

    return true; // 競合がない場合は追加可能
  };

  // 車両選択時の重複チェックと確認
  const checkVehicleConflict = async (car: Car) => {
    // 現在編集中の予定のID（新規の場合はundefined）
    const currentScheduleId = scheduleType === 'construction'
      ? constructionFormData.id
      : otherFormData.id;

    // 現在の日付
    const currentDate = scheduleType === 'construction'
      ? constructionFormData.scheduleDate
      : otherFormData.scheduleDate;

    if (!currentDate) return true; // 日付がない場合は競合なしとみなす

    // 同じ日付の全ての予定を取得
    const dailySchedules = await fetchDailySchedules(currentDate);

    // 同じ車両が登録されている他の予定を検索
    const conflictingSchedules = [];

    // 工事予定をチェック
    for (const schedule of dailySchedules.construction) {
      if (schedule.id !== currentScheduleId && schedule.vehicleData &&
        schedule.vehicleData.some((v: any) => v.Car_ID === car.ID)) {
        conflictingSchedules.push({
          type: '工事予定',
          title: schedule.siteName || '(工事名なし)',
          time: schedule.isAllDay ? '終日' : `${schedule.startTime || ''}～${schedule.endTime || ''}`
        });
      }
    }

    // その他予定をチェック
    for (const schedule of dailySchedules.other) {
      if (schedule.id !== currentScheduleId && schedule.vehicleData &&
        schedule.vehicleData.some((v: any) => v.Car_ID === car.ID)) {
        conflictingSchedules.push({
          type: 'その他予定',
          title: schedule.scheduleContent || '(内容なし)',
          time: schedule.isAllDay ? '終日' : `${schedule.startTime || ''}～${schedule.endTime || ''}`
        });
      }
    }

    // 競合がある場合は警告を表示
    if (conflictingSchedules.length > 0) {
      const carDisplayName = car.Car_Nickname || `${car.Car_Model} ${car.Car_Number_1}${car.Car_Number_2} ${car.Car_Number_3}${car.Car_Number_4}`;
      const confirmAdd = window.confirm(
        `${carDisplayName}は既に同日の以下の予定に登録されています:\n\n` +
        conflictingSchedules.map(s => `・${s.type}: ${s.title} (${s.time})`).join('\n') +
        '\n\n追加しますか？'
      );

      return confirmAdd; // 確認結果を返す
    }

    return true; // 競合がない場合は追加可能
  };

  return {
    membersWithOtherSchedules,
    vehiclesWithOtherSchedules,
    checkMemberConflict,
    checkVehicleConflict
  };
};